import { useState } from "react";
import AppCommunityChat from "./AppCommunityChat";
import AppCommunityGamification from "./AppCommunityGamification";
import AppEventsTab from "./AppEventsTab";
import AppContestsTab from "./AppContestsTab";
import AppWinnersTab from "./AppWinnersTab";
import { globalClubActivityFeed, activityTypeConfig } from "@/mocks/rfwClubs";
import { adminGreetings, statusLabels as greetingStatusLabels, statusColors as greetingStatusColors } from "@/mocks/adminGreetings";
import { songRequests, statusLabels as songStatusLabels, statusColors as songStatusColors } from "@/mocks/adminSongRequests";
import { getActivePolls, getPollById, formatCount as pollFormatCount, getTimeRemainingText } from "@/mocks/rfwPolls";
import type { Poll } from "@/mocks/rfwPolls";
import type { GreetingStatus } from "@/mocks/adminGreetings";
import type { SongRequestStatus } from "@/mocks/adminSongRequests";
import Swal from "sweetalert2";

type CommunityTab = "chat" | "comunidad" | "saludos" | "canciones" | "gamificacion" | "encuestas" | "eventos" | "concursos" | "ganadores";

export default function AppCommunityTab() {
  const [activeTab, setActiveTab] = useState<CommunityTab>("chat");

  // ── Polls state ──
  const [pollVotes, setPollVotes] = useState<Record<string, { optionId: string; voted: boolean }>>({});
  const [pollResults, setPollResults] = useState<Record<string, Poll["options"]>>({});
  const [selectedPollId, setSelectedPollId] = useState<string | null>(null);

  const activePolls = getActivePolls();
  const selectedPoll = selectedPollId ? getPollById(selectedPollId) : null;

  // ── Greetings state ──
  const [greetingTo, setGreetingTo] = useState("");
  const [greetingMessage, setGreetingMessage] = useState("");
  const [greetingRadio, setGreetingRadio] = useState("Radio Éxito FM");
  const [sentGreetings, setSentGreetings] = useState<Array<{
    id: number; to: string; message: string; radio: string; status: GreetingStatus; timestamp: string;
  }>>([]);

  // ── Song requests state ──
  const [songName, setSongName] = useState("");
  const [songArtist, setSongArtist] = useState("");
  const [songDedication, setSongDedication] = useState("");
  const [songRadio, setSongRadio] = useState("Radio Éxito FM");
  const [sentSongs, setSentSongs] = useState<Array<{
    id: number; songName: string; artistName: string; dedication: string; radio: string; status: SongRequestStatus; timestamp: string;
  }>>([]);

  const handleVote = (pollId: string, optionId: string) => {
    if (pollVotes[pollId]?.voted) {
      Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Ya votaste en esta encuesta", showConfirmButton: false, timer: 2000, timerProgressBar: true });
      return;
    }

    const poll = getPollById(pollId);
    if (!poll) return;

    const updatedOptions = (pollResults[pollId] || poll.options).map((opt) => {
      if (opt.id === optionId) {
        const newVotes = opt.votes + 1;
        const newTotal = poll.totalVotes + 1;
        return { ...opt, votes: newVotes, percentage: Math.round((newVotes / newTotal) * 1000) / 10 };
      }
      const newTotal = poll.totalVotes + 1;
      return { ...opt, percentage: Math.round((opt.votes / newTotal) * 1000) / 10 };
    });

    setPollResults((prev) => ({ ...prev, [pollId]: updatedOptions }));
    setPollVotes((prev) => ({ ...prev, [pollId]: { optionId, voted: true } }));
  };

  const handleSendGreeting = () => {
    if (!greetingTo.trim() || !greetingMessage.trim()) {
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Completá todos los campos", showConfirmButton: false, timer: 2000, timerProgressBar: true });
      return;
    }
    const newGreeting = {
      id: Date.now(),
      to: greetingTo.trim(),
      message: greetingMessage.trim(),
      radio: greetingRadio,
      status: "nuevo" as GreetingStatus,
      timestamp: new Date().toISOString(),
    };
    setSentGreetings((prev) => [newGreeting, ...prev]);
    setGreetingTo("");
    setGreetingMessage("");
    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Saludo enviado! Llega a Admin Radio", showConfirmButton: false, timer: 2000, timerProgressBar: true });
  };

  const handleSendSong = () => {
    if (!songName.trim() || !songArtist.trim()) {
      Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Completá nombre y artista", showConfirmButton: false, timer: 2000, timerProgressBar: true });
      return;
    }
    const newSong = {
      id: Date.now(),
      songName: songName.trim(),
      artistName: songArtist.trim(),
      dedication: songDedication.trim(),
      radio: songRadio,
      status: "pendiente" as SongRequestStatus,
      timestamp: new Date().toISOString(),
    };
    setSentSongs((prev) => [newSong, ...prev]);
    setSongName("");
    setSongArtist("");
    setSongDedication("");
    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Canción solicitada! Llega a Admin Radio", showConfirmButton: false, timer: 2000, timerProgressBar: true });
  };

  const tabs: { id: CommunityTab; label: string; icon: string }[] = [
    { id: "chat", label: "Chat", icon: "ri-chat-3-line" },
    { id: "comunidad", label: "Comunidad", icon: "ri-group-line" },
    { id: "saludos", label: "Saludos", icon: "ri-message-2-line" },
    { id: "canciones", label: "Canciones", icon: "ri-music-line" },
    { id: "gamificacion", label: "Gaming", icon: "ri-award-line" },
    { id: "encuestas", label: "Encuestas", icon: "ri-bar-chart-horizontal-line" },
    { id: "eventos", label: "Eventos", icon: "ri-calendar-event-line" },
    { id: "concursos", label: "Concursos", icon: "ri-trophy-line" },
    { id: "ganadores", label: "Ganadores", icon: "ri-vip-crown-line" },
  ];

  const allGreetings = [...sentGreetings, ...adminGreetings.map((g) => ({
    id: g.id,
    to: g.senderName,
    message: g.content,
    radio: g.location,
    status: g.status,
    timestamp: g.timestamp,
  }))].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const allSongs = [...sentSongs, ...songRequests.map((s) => ({
    id: s.id,
    songName: s.songName,
    artistName: s.artistName,
    dedication: s.dedication || "",
    radio: s.requesterLocation,
    status: s.status,
    timestamp: s.requestedAt,
  }))].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <div className="h-full flex flex-col bg-background-50">
      {/* Header */}
      <div className="px-4 py-3 border-b border-background-200 shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
            <i className="ri-group-fill text-primary-500 text-base"></i>
          </div>
          <div>
            <h1 className="text-base font-bold text-foreground-950">Comunidad</h1>
            <p className="text-[10px] text-foreground-500">Chat, saludos, rankings y más</p>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 mt-2.5 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              <i className={`${tab.icon} text-[12px]`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {/* Chat */}
        {activeTab === "chat" && <AppCommunityChat />}

        {/* Comunidad - Activity Feed */}
        {activeTab === "comunidad" && (
          <div className="h-full overflow-y-auto px-4 py-3 scrollbar-none space-y-2">
            <h3 className="text-xs font-bold text-foreground-950 mb-2">Actividad Reciente en Clubes</h3>
            {globalClubActivityFeed.map((activity) => {
              const cfg = activityTypeConfig[activity.type];
              return (
                <div key={activity.id} className="flex items-start gap-2.5 bg-background-50 border border-background-200 rounded-2xl p-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img src={activity.user.avatarUrl} alt={activity.user.name} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-bold text-foreground-900">{activity.user.name}</span>
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    <p className="text-[10px] text-foreground-600 mt-0.5">{activity.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] text-foreground-400">{activity.timestamp}</span>
                      <span className="text-[9px] text-accent-500 font-semibold">+{activity.points} pts</span>
                      {activity.targetTitle && (
                        <span className="text-[9px] text-foreground-400">· {activity.targetTitle}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Saludos / Greetings */}
        {activeTab === "saludos" && (
          <div className="h-full overflow-y-auto px-4 py-3 scrollbar-none space-y-3">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2 flex items-center gap-1.5">
                <i className="ri-message-2-line text-primary-500"></i>Enviar Saludo
              </h3>
              <div className="space-y-2">
                <input type="text" value={greetingTo} onChange={(e) => setGreetingTo(e.target.value)} placeholder="¿A quién va dirigido?" className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400" />
                <textarea value={greetingMessage} onChange={(e) => setGreetingMessage(e.target.value)} placeholder="Escribí tu saludo..." maxLength={500} rows={3} className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 resize-none" />
                <select value={greetingRadio} onChange={(e) => setGreetingRadio(e.target.value)} className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 focus:outline-none focus:border-primary-400">
                  {["Radio Éxito FM", "Estéreo Capital", "Radio Deportiva Total", "La Kalle 99.9", "Radio Peninsular", "Rock Total Radio"].map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
                <button onClick={handleSendGreeting} className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-bold cursor-pointer whitespace-nowrap transition-all">
                  <i className="ri-send-plane-fill mr-1.5"></i>Enviar Saludo
                </button>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold text-foreground-950 mb-2">Saludos ({allGreetings.length})</h3>
              <div className="space-y-1.5">
                {allGreetings.slice(0, 15).map((g) => {
                  const statusCfg = greetingStatusColors[g.status] || greetingStatusColors.nuevo;
                  return (
                    <div key={g.id} className={`rounded-xl p-2.5 border ${statusCfg.bg}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-bold text-foreground-900 truncate">{g.to}</span>
                        <span className={`text-[8px] font-semibold ${statusCfg.text} flex items-center gap-1`}><span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`}></span>{greetingStatusLabels[g.status]}</span>
                      </div>
                      <p className="text-[10px] text-foreground-600 line-clamp-2">{g.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[8px] text-foreground-400">{g.radio}</span>
                        <span className="text-[8px] text-foreground-400">{new Date(g.timestamp).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Canciones / Song Requests */}
        {activeTab === "canciones" && (
          <div className="h-full overflow-y-auto px-4 py-3 scrollbar-none space-y-3">
            <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
              <h3 className="text-xs font-bold text-foreground-950 mb-2 flex items-center gap-1.5"><i className="ri-music-line text-accent-500"></i>Solicitar Canción</h3>
              <div className="space-y-2">
                <input type="text" value={songName} onChange={(e) => setSongName(e.target.value)} placeholder="Nombre de la canción" className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400" />
                <input type="text" value={songArtist} onChange={(e) => setSongArtist(e.target.value)} placeholder="Artista" className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400" />
                <input type="text" value={songDedication} onChange={(e) => setSongDedication(e.target.value)} placeholder="Dedicatoria (opcional)" className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400" />
                <select value={songRadio} onChange={(e) => setSongRadio(e.target.value)} className="w-full bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 focus:outline-none focus:border-primary-400">
                  {["Radio Éxito FM", "Estéreo Capital", "Radio Deportiva Total", "La Kalle 99.9", "Radio Peninsular", "Rock Total Radio"].map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
                <button onClick={handleSendSong} className="w-full py-2.5 rounded-xl bg-accent-500 hover:bg-accent-600 text-background-50 text-xs font-bold cursor-pointer whitespace-nowrap transition-all">
                  <i className="ri-send-plane-fill mr-1.5"></i>Solicitar Canción
                </button>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-bold text-foreground-950 mb-2">Solicitudes ({allSongs.length})</h3>
              <div className="space-y-1.5">
                {allSongs.slice(0, 15).map((s) => {
                  const statusCfg = songStatusColors[s.status] || songStatusColors.pendiente;
                  return (
                    <div key={s.id} className={`rounded-xl p-2.5 border ${statusCfg.bg}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="min-w-0 flex-1"><span className="text-[10px] font-bold text-foreground-900">{s.songName}</span><span className="text-[9px] text-foreground-500 ml-1">· {s.artistName}</span></div>
                        <span className={`text-[8px] font-semibold ${statusCfg.text} flex items-center gap-1 shrink-0`}><i className={`${statusCfg.icon} text-[10px]`}></i>{songStatusLabels[s.status]}</span>
                      </div>
                      {s.dedication && <p className="text-[9px] text-foreground-500 line-clamp-1 mt-0.5">{s.dedication}</p>}
                      <div className="flex items-center gap-2 mt-1"><span className="text-[8px] text-foreground-400">{s.radio}</span><span className="text-[8px] text-foreground-400">{new Date(s.timestamp).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Gamificación */}
        {activeTab === "gamificacion" && <AppCommunityGamification />}

        {/* Encuestas / Polls */}
        {activeTab === "encuestas" && (
          <div className="h-full overflow-y-auto px-4 py-3 scrollbar-none">
            {selectedPoll ? (
              <div className="space-y-3">
                <button onClick={() => setSelectedPollId(null)} className="flex items-center gap-1 text-[10px] text-foreground-500 hover:text-foreground-700 cursor-pointer mb-1"><i className="ri-arrow-left-s-line"></i> Volver a encuestas</button>
                <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
                  <div className="flex items-center gap-2 mb-2">
                    <img src={selectedPoll.entityLogoUrl} alt="" className="w-8 h-8 rounded-lg object-cover object-top" loading="lazy" />
                    <div><h3 className="text-sm font-bold text-foreground-950">{selectedPoll.question}</h3><p className="text-[9px] text-foreground-500">{selectedPoll.entityName} · Cierra {getTimeRemainingText(selectedPoll.closesAt)}</p></div>
                  </div>
                  {selectedPoll.description && <p className="text-[10px] text-foreground-600 mb-3">{selectedPoll.description}</p>}
                  <div className="space-y-2">
                    {(pollResults[selectedPoll.id] || selectedPoll.options).map((opt) => {
                      const isSelected = pollVotes[selectedPoll.id]?.optionId === opt.id;
                      return (
                        <button key={opt.id} onClick={() => handleVote(selectedPoll.id, opt.id)} disabled={pollVotes[selectedPoll.id]?.voted}
                          className={`w-full text-left rounded-xl p-3 border transition-all cursor-pointer ${isSelected ? "border-primary-400 bg-primary-50" : "border-background-200 bg-background-50 hover:border-foreground-300"} ${pollVotes[selectedPoll.id]?.voted ? "cursor-default" : ""}`}>
                          <div className="flex items-center justify-between mb-1"><span className="text-[11px] font-semibold text-foreground-900">{opt.text}</span><span className="text-[11px] font-bold text-foreground-700">{opt.percentage}%</span></div>
                          <div className="h-2 rounded-full bg-background-200 overflow-hidden"><div className={`h-full rounded-full transition-all ${isSelected ? "bg-primary-500" : "bg-foreground-300"}`} style={{ width: `${opt.percentage}%` }}></div></div>
                          <span className="text-[9px] text-foreground-500 mt-0.5">{pollFormatCount(opt.votes)} votos</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-background-200">
                    <span className="text-[10px] text-foreground-500">{pollFormatCount(pollVotes[selectedPoll.id]?.voted ? selectedPoll.totalVotes + 1 : selectedPoll.totalVotes)} votos totales</span>
                    {pollVotes[selectedPoll.id]?.voted && <span className="text-[10px] text-primary-500 font-semibold">¡Ya votaste!</span>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <h3 className="text-xs font-bold text-foreground-950 mb-2">Encuestas Activas ({activePolls.length})</h3>
                {activePolls.slice(0, 12).map((poll) => (
                  <button key={poll.id} onClick={() => setSelectedPollId(poll.id)} className="w-full text-left bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-3.5 transition-all cursor-pointer">
                    <div className="flex items-center gap-2 mb-1.5">
                      <img src={poll.entityLogoUrl} alt="" className="w-7 h-7 rounded-lg object-cover object-top" loading="lazy" />
                      <div className="min-w-0 flex-1"><h4 className="text-xs font-bold text-foreground-900 line-clamp-2">{poll.question}</h4><p className="text-[9px] text-foreground-500">{poll.entityName} · {pollFormatCount(poll.totalVotes)} votos</p></div>
                      <span className="text-[9px] text-foreground-400 shrink-0">{getTimeRemainingText(poll.closesAt)}</span>
                    </div>
                    {poll.options.slice(0, 1).map((opt) => (
                      <div key={opt.id} className="flex items-center gap-2"><div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden"><div className="h-full rounded-full bg-primary-400" style={{ width: `${opt.percentage}%` }}></div></div><span className="text-[9px] font-bold text-foreground-600">{opt.percentage}%</span></div>
                    ))}
                    {pollVotes[poll.id]?.voted && <span className="text-[9px] text-primary-500 font-semibold mt-1 inline-block">Ya votaste ✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Eventos */}
        {activeTab === "eventos" && <AppEventsTab />}

        {/* Concursos */}
        {activeTab === "concursos" && <AppContestsTab />}

        {/* Ganadores */}
        {activeTab === "ganadores" && <AppWinnersTab />}
      </div>
    </div>
  );
}